import type { Service } from '@/types'

export const services: Service[] = [
    {
        slug: 'party-makeovers',
        name: "Makeovers",
        title: "Party Makeovers & Draping",
        heading: "Party Makeovers & Draping in North Delhi",
        short: "Soft, classic or full glam looks for parties, engagements, sangeet and every celebration, finished with neat saree or dupatta draping.",
        description: "Whether it is a family function, a reception, a festival or a farewell, our makeup artists create a look that suits your face, your outfit and the occasion. We begin with skin prep for a smooth base, then build your eyes, lips and contour to match your style, from soft and natural to full glam. Complete it with saree or dupatta draping and a matching hairstyle, and you are ready for every photo.",
        highlights: [
            "Party, engagement and reception makeup",
            "Soft glam, natural and full glam looks",
            "Saree, lehenga and dupatta draping",
            "Eye makeup, lashes and contouring",
            "Photo-ready finish for your event",
            "Matching hairstyle on request"
        ],
        details: [
            { title: "Party makeup", text: "From day functions to evening receptions, a look matched to your outfit, skin tone and lighting." },
            { title: "Engagement and sangeet looks", text: "Graceful makeup for your pre-wedding functions, soft and elegant or bold and glamorous." },
            { title: "Saree and dupatta draping", text: "Neat, secure draping in the style you love, so your outfit sits beautifully all evening." },
            { title: "Eye makeup and lashes", text: "Smokey, glitter, cut-crease or subtle eyes, finished with lashes to open up your look." },
            { title: "Hairstyle to match", text: "Curls, buns, braids or soft waves styled to complete your makeover." },
            { title: "Family and group looks", text: "Getting ready with family or friends? Tell us when you book and we will plan time for everyone." }
        ],
        occasions: [
            "Weddings and receptions",
            "Engagement and roka",
            "Sangeet and mehendi",
            "Festivals and pujas",
            "Birthdays and anniversaries",
            "Farewells and photoshoots"
        ],
        faqs: [
            {
                question: "How early should I book a party makeover?",
                answer: "For weekends, festivals and the wedding season, booking a few days in advance is best. For a same-day slot, message us on WhatsApp and we will tell you what is available."
            },
            {
                question: "Can I get saree or dupatta draping with my makeover?",
                answer: "Yes. Saree, lehenga and dupatta draping is available with your makeover. Tell us about your outfit when you book so we can plan the right style."
            },
            {
                question: "What are the charges for a party makeover?",
                answer: "Charges depend on the look and the services you choose. Send us your requirement on WhatsApp or call us and we will share the details."
            }
        ],
        image: '/images/makeover.jpg',
        imageAlt: "Glamorous party makeover with shimmering eyeshadow and glossy lips",
        imageClassName: 'object-[50%_40%]',
        cover: '/images/makeover.jpg',
        coverAlt: "Party makeup look with shimmering eyes and glossy peach lips",
        coverClassName: 'object-[50%_40%]',
        extraImage: '/images/bridal-makeup.jpg',
        extraImageAlt: "Makeup artist applying powder with a brush for a flawless base",
        whatsappMessage: "Hello Anushka Beauty Parlour, I would like to book a party makeover.",
        seoTitle: "Party makeovers and draping",
        seoDescription: "Party makeup, engagement and sangeet looks with saree and dupatta draping at Anushka Beauty Parlour, a women's parlour in New Gupta Colony, North Delhi.",
        keywords: [
            "Party Makeovers & Draping",
            "party makeup artist near me",
            "saree draping near me",
            "engagement makeup artist Delhi",
            "party makeup in Model Town",
            "party makeup in GTB Nagar"
        ]
    },
    {
        slug: 'bridal-makeup',
        name: "Bridal Makeups",
        title: "HD & Airbrush Bridal Looks",
        heading: "Bridal Makeup in New Gupta Colony, Delhi",
        short: "Flawless HD and airbrush bridal makeup with bridal hairstyling, dupatta setting and draping for your big day.",
        description: "Your wedding look should feel like you, only more radiant. Our bridal makeup artists plan your look around your lehenga, jewellery, skin type and the timing of your ceremony. Choose HD makeup for a natural, skin-like finish or airbrush makeup for a light, even base that looks soft and flawless. We complete the look with bridal hairstyling, dupatta setting and jewellery placement, so you can relax and enjoy every moment.",
        highlights: [
            "HD bridal makeup",
            "Airbrush bridal makeup",
            "Bridal hairstyle and dupatta setting",
            "Engagement, mehendi and reception looks",
            "Pre-bridal skin care",
            "Makeup for family and bridesmaids"
        ],
        details: [
            { title: "HD bridal makeup", text: "A lightweight, skin-like finish that looks natural in person and beautiful in HD photos and videos." },
            { title: "Airbrush bridal makeup", text: "A smooth, even base applied in fine layers for a soft-focus, flawless look through your ceremony." },
            { title: "Bridal hair and dupatta setting", text: "Traditional buns, braids with flowers or modern open styles, with your dupatta pinned securely in place." },
            { title: "Jewellery and draping", text: "Maang tikka, nath and necklace placed perfectly, and your lehenga or saree draped neatly." },
            { title: "Pre-bridal care", text: "Facials, cleanups, skin treatments and nail art in the weeks before your wedding, so you glow on the day." },
            { title: "Family and bridesmaids", text: "Makeup and hairstyling for your mother, sisters and friends so the whole family looks picture-perfect." }
        ],
        occasions: [
            "Wedding and pheras",
            "Reception",
            "Engagement and roka",
            "Mehendi and haldi",
            "Sangeet",
            "Pre-wedding shoot"
        ],
        faqs: [
            {
                question: "What is the difference between HD and airbrush bridal makeup?",
                answer: "HD makeup uses fine, light-reflecting products for a natural, skin-like finish. Airbrush makeup is applied in very thin layers for an even, soft-focus base. Our artists will suggest the best option for your skin and your wedding schedule."
            },
            {
                question: "When should I book my bridal makeup?",
                answer: "As early as you can, especially in the wedding season. Share your wedding date with us on WhatsApp and we will reserve your slot."
            },
            {
                question: "Do you also do makeup for the bride's family?",
                answer: "Yes. Makeup and hairstyling for mothers, sisters, bridesmaids and guests can be booked along with the bride's look."
            },
            {
                question: "What are your bridal makeup charges?",
                answer: "Bridal charges depend on the look you choose (HD or airbrush) and the services you add. Message us on WhatsApp with your date and requirement for complete details."
            }
        ],
        image: '/images/bridal-makeup.jpg',
        imageAlt: "Makeup artist applying an HD makeup base with a brush",
        imageClassName: 'object-[60%_45%]',
        cover: '/images/hero-bride.jpg',
        coverAlt: "Indian bride in a red embroidered lehenga with gold bridal jewellery",
        coverClassName: 'object-[50%_15%]',
        extraImage: '/images/salon-interior.jpg',
        extraImageAlt: "Elegant white and gold salon with comfortable chairs and mirrors",
        whatsappMessage: "Hello Anushka Beauty Parlour, I would like to book bridal makeup. My wedding date is ",
        seoTitle: "Bridal makeup in New Gupta Colony",
        seoDescription: "HD and airbrush bridal makeup in New Gupta Colony, North Delhi with bridal hairstyles, dupatta setting, draping and pre-bridal care. Book on WhatsApp.",
        keywords: [
            "Bridal Makeups in New Gupta Colony",
            "HD & Airbrush Bridal Looks",
            "bridal beauty makeup artist Delhi",
            "bridal makeup in Model Town",
            "bridal makeup in GTB Nagar",
            "airbrush bridal makeup North Delhi"
        ]
    },
    {
        slug: 'skin-treatments',
        name: "Skin Treatments",
        title: "Organic Skin Treatments",
        heading: "Organic Skin Treatments in Delhi",
        short: "Gentle treatments for tan, dullness, dryness and uneven skin, chosen after understanding your skin type.",
        description: "We care about your skin. Before any treatment, we look at your skin type and concerns, then recommend care that is gentle and effective. From de-tan and brightening to hydration and pre-bridal glow, every treatment uses only high quality products, with organic and herbal options for sensitive skin.",
        highlights: [
            "Skin consultation before every treatment",
            "De-tan and brightening care",
            "Hydration for dry and dull skin",
            "Organic and herbal options",
            "Pre-bridal glow treatments",
            "Gentle care for oily skin"
        ],
        details: [
            { title: "Skin consultation", text: "We understand your skin type, routine and concerns first, so the treatment suits you." },
            { title: "De-tan and brightening", text: "Helps lift sun tan and dullness for a fresher, more even-looking complexion." },
            { title: "Hydrating treatment", text: "Replenishes moisture so skin feels soft, smooth and comfortable." },
            { title: "Organic and herbal care", text: "Gentle options made with natural ingredients, ideal for sensitive skin." },
            { title: "Pre-bridal glow", text: "A planned series of treatments in the weeks before your wedding or big event." },
            { title: "Oil control care", text: "Deep cleansing and balancing care for oily skin and clogged pores." }
        ],
        faqs: [
            {
                question: "Which skin treatment is right for me?",
                answer: "It depends on your skin type and what you would like to improve. Visit us or message us on WhatsApp and we will suggest the right treatment for you."
            },
            {
                question: "Do you use organic products?",
                answer: "Yes. Organic and herbal options are available, and we use only high quality products for every skin treatment."
            },
            {
                question: "How often should I get a skin treatment?",
                answer: "Many clients come every three to four weeks. For pre-bridal care, we plan a schedule around your wedding date."
            }
        ],
        image: '/images/skin-treatment.jpg',
        imageAlt: "Woman relaxing during a gentle skin treatment at a beauty parlour",
        imageClassName: 'object-[45%_50%]',
        cover: '/images/skin-treatment.jpg',
        coverAlt: "Skin treatment being given to a relaxed client",
        coverClassName: 'object-[45%_50%]',
        extraImage: '/images/facial-cleanup.jpg',
        extraImageAlt: "Beautician giving a soothing face massage",
        whatsappMessage: "Hello Anushka Beauty Parlour, I would like to know about your skin treatments.",
        seoTitle: "Organic skin treatments in Delhi",
        seoDescription: "Organic skin treatments, de-tan, brightening, hydration and pre-bridal glow care at Anushka Beauty Parlour in New Gupta Colony, North Delhi. Women only.",
        keywords: [
            "Organic Skin Treatments",
            "skin treatment Delhi",
            "de-tan treatment near me",
            "pre-bridal skin care North Delhi",
            "skin care parlour in Model Town"
        ]
    },
    {
        slug: 'nail-art',
        name: "Nail Art & Nail Treatment",
        title: "Designer Nail Arts & Extensions",
        heading: "Designer Nail Art & Extensions in North Delhi",
        short: "Designer and bridal nail art, nail extensions, gel polish and caring nail treatments by our experienced nail artist.",
        description: "Beautiful nails complete every look. Our experienced nail artist creates everything from minimal French tips to bridal nail art with glitter, stones and hand-painted designs. Choose extensions for length and shape, or gel polish for a glossy, chip-resistant finish. We also look after the health of your nails with cleaning, shaping and cuticle care.",
        highlights: [
            "Designer and bridal nail art",
            "Nail extensions in your choice of shape",
            "Gel polish and French tips",
            "Glitter, stones and 3D designs",
            "Cleaning, shaping and cuticle care",
            "Refills and nail repair"
        ],
        details: [
            { title: "Designer nail art", text: "Hand-painted patterns, florals, ombre and trending designs created to match your style or outfit." },
            { title: "Nail extensions", text: "Add length and shape, from natural almond to bold coffin, with a strong and neat finish." },
            { title: "Bridal nail art", text: "Elegant designs with glitter, stones and gold accents to match your bridal look." },
            { title: "Gel polish", text: "A glossy, chip-resistant colour that keeps your nails looking fresh for longer." },
            { title: "Nail care treatment", text: "Cleaning, filing, shaping and cuticle care for healthy, tidy nails." },
            { title: "Refills and repairs", text: "Maintenance for extensions and quick fixes for chipped or broken nails." }
        ],
        faqs: [
            {
                question: "Is there a nail art salon near me in North Delhi?",
                answer: "Yes. Anushka Beauty Parlour in New Gupta Colony offers designer nail art, extensions and nail care, close to Model Town, GTB Nagar, Vijay Nagar and Kamla Nagar."
            },
            {
                question: "Can I show you a nail design I like?",
                answer: "Of course. Share a picture with us on WhatsApp or bring it along, and our nail artist will create it for you."
            },
            {
                question: "How long do nail extensions last?",
                answer: "With good care, extensions stay neat for a few weeks, after which a refill keeps them looking fresh."
            }
        ],
        image: '/images/nail-extensions.jpg',
        imageAlt: "Pink floral nail art with gold glitter and stones",
        imageClassName: 'object-[50%_45%]',
        cover: '/images/nail-extensions.jpg',
        coverAlt: "Designer nail extensions with pink florals and gold glitter",
        coverClassName: 'object-[50%_45%]',
        extraImage: '/images/nail-art.jpg',
        extraImageAlt: "Bright geometric nail art in pink, yellow and blue",
        whatsappMessage: "Hello Anushka Beauty Parlour, I would like to book nail art. Here is a design I like: ",
        seoTitle: "Designer nail art and extensions",
        seoDescription: "Designer nail art, nail extensions, gel polish and nail care at Anushka Beauty Parlour, your nail salon near Model Town, GTB Nagar and Vijay Nagar, Delhi.",
        keywords: [
            "Designer Nail Arts & Extensions",
            "nail salon near me",
            "nail arts nearby",
            "nail extensions North Delhi",
            "bridal nail art Delhi"
        ]
    },
    {
        slug: 'facial-cleanup',
        name: "Facial Cleanup",
        title: "Facials, Cleanup & De-tan",
        heading: "Facial Cleanup & Glow Facials Near You",
        short: "Relaxing facials and deep cleanups that clear pores, tan and dullness for fresh, glowing skin.",
        description: "A good cleanup is the easiest way to bring back your glow. Our facial cleanups deep-cleanse the pores, gently exfoliate, clear blackheads and finish with a soothing mask and massage. For special occasions, choose a glow facial for extra radiance. Every service uses only high quality products suited to your skin type.",
        highlights: [
            "Deep cleansing cleanup",
            "Glow, fruit and gold facials",
            "Blackhead and whitehead removal",
            "De-tan and bleach",
            "Relaxing face and neck massage",
            "Gentle facials for sensitive skin"
        ],
        details: [
            { title: "Classic cleanup", text: "Cleansing, steam, exfoliation and extraction to clear clogged pores and refresh your skin." },
            { title: "Glow facial", text: "A complete facial with massage and mask for instant brightness before an event." },
            { title: "Fruit and gold facials", text: "Much-loved facials that leave your skin soft, nourished and radiant." },
            { title: "De-tan and bleach", text: "Helps reduce tan and even out skin tone on the face and neck." },
            { title: "Blackhead removal", text: "Careful, hygienic extraction to keep pores clean and skin smooth." },
            { title: "Relaxing massage", text: "A soothing face, neck and shoulder massage to help you unwind." }
        ],
        faqs: [
            {
                question: "Is there a facial parlour near me in North Delhi?",
                answer: "Yes. We are in New Gupta Colony, Kalyan Vihar, a short ride from Model Town, GTB Nagar, Hudson Lane and Kamla Nagar. Walk in or book on WhatsApp."
            },
            {
                question: "What is the difference between a cleanup and a facial?",
                answer: "A cleanup focuses on cleansing, exfoliating and clearing pores. A facial adds a longer massage, masks and nourishing care for a deeper glow and relaxation."
            },
            {
                question: "How often should I get a cleanup?",
                answer: "Every two to four weeks keeps pores clear and skin fresh. We will suggest a routine that suits your skin."
            }
        ],
        image: '/images/facial-cleanup.jpg',
        imageAlt: "Woman enjoying a relaxing facial cleanup and face massage",
        imageClassName: 'object-[55%_50%]',
        cover: '/images/facial-cleanup.jpg',
        coverAlt: "Facial cleanup with a soothing forehead massage",
        coverClassName: 'object-[55%_50%]',
        extraImage: '/images/skin-treatment.jpg',
        extraImageAlt: "Gentle skin care treatment at the parlour",
        whatsappMessage: "Hello Anushka Beauty Parlour, I would like to book a facial cleanup.",
        seoTitle: "Facial cleanup and facials near you",
        seoDescription: "Facial cleanup, glow facials, de-tan and bleach at Anushka Beauty Parlour, a women's facial parlour in New Gupta Colony near Model Town and GTB Nagar, Delhi.",
        keywords: [
            "Facial Cleanup",
            "facial parlour near me",
            "glow facial in North Delhi",
            "de-tan facial near me",
            "facial in Model Town"
        ]
    },
    {
        slug: 'hair-care',
        name: "Hair Care & Hair Designs",
        title: "Hair Care & Bridal Hair Designs",
        heading: "Hair Care, Keratin & Bridal Hair Designs",
        short: "Hair spa, keratin, smoothening and stylish cuts, plus bridal and party hairstyles that stay beautiful all day.",
        description: "Healthy hair is beautiful hair. We offer nourishing hair spa and keratin treatment for frizz-free, shiny hair, smoothening, straightening and rebonding for a sleek look, and haircuts that suit your face. For weddings and parties, our hair designs range from elegant buns with flowers and braids to soft curls and open waves, styled to match your outfit and makeup.",
        highlights: [
            "Hair spa and deep conditioning",
            "Keratin treatment",
            "Smoothening, straightening and rebonding",
            "Haircuts and blow-dry styling",
            "Bridal buns, braids and floral styles",
            "Party curls, waves and updos"
        ],
        details: [
            { title: "Hair spa", text: "Deep conditioning and a relaxing scalp massage to nourish dry, damaged or frizzy hair." },
            { title: "Keratin treatment", text: "Smooths frizz and adds shine for hair that is easier to manage every day." },
            { title: "Smoothening and rebonding", text: "Sleek, straight hair with careful, professional application." },
            { title: "Haircuts and styling", text: "Cuts, trims and blow-dries that flatter your face shape and lifestyle." },
            { title: "Bridal hair designs", text: "Traditional buns, braids with flowers and modern styles to complete your bridal look." },
            { title: "Party hairstyles", text: "Curls, waves, puffs and updos that stay in place through your celebration." }
        ],
        faqs: [
            {
                question: "Do you do hair keratin treatment?",
                answer: "Yes. Keratin treatment is available along with hair spa, smoothening, straightening and rebonding. We check your hair type first and suggest the best option."
            },
            {
                question: "Do you do hairstyles for weddings and parties?",
                answer: "Yes. Our hair designs include bridal buns, floral braids, curls, waves and updos, styled to match your outfit and makeup."
            },
            {
                question: "How do I care for my hair after a keratin treatment?",
                answer: "We share simple aftercare tips after your treatment, such as using a gentle, sulphate-free shampoo, so your results last longer."
            }
        ],
        image: '/images/hair-care.jpg',
        imageAlt: "Elegant textured updo hairstyle",
        imageClassName: 'object-[50%_25%]',
        cover: '/images/hair-care.jpg',
        coverAlt: "Elegant updo hair design with soft curls",
        coverClassName: 'object-[50%_20%]',
        extraImage: '/images/salon-interior.jpg',
        extraImageAlt: "Bright salon with styling chairs and mirrors",
        whatsappMessage: "Hello Anushka Beauty Parlour, I would like to know about your hair care services.",
        seoTitle: "Hair care, keratin and bridal hair designs",
        seoDescription: "Hair spa, keratin, smoothening, rebonding, haircuts and bridal hair designs at Anushka Beauty Parlour, New Gupta Colony, North Delhi. Women-only parlour.",
        keywords: [
            "Hair Care & Bridal Hair Designs",
            "hair keratin treatment",
            "hair smoothening near me",
            "bridal hairstyle North Delhi",
            "hair spa in Model Town"
        ]
    }
]

export const getServiceBySlug = (slug: string): Service | undefined => {
    return services.find(service => service.slug === slug)
}
